import { useApiUtils } from 'x1-base/composables';
import { useCustomFetch } from '~/composables';
import type { NitroFetchOptions } from 'nitropack';
import type {
    Review,
    ReviewSubmitResponse,
    ReviewSummary
} from '~/types';
import type {
    PaginatedPayload,
    CustomPagination,
    PaginatedRequest
} from 'x1-base/types';
import { merge as lo_mergeCompat } from 'es-toolkit/compat';
import { isString as lo_isString, omit as lo_omit } from 'es-toolkit';
import type { NuxtError } from '#app';

// The reviews endpoints throttle per IP, and a 429 can mean anything from a
// moment to a full hour. `Retry-After` (seconds) is CORS-exposed by the API, so
// say something true when it is there and stay vague only when it is not —
// understating an hour makes a customer retry at once and conclude it is broken.
export function getThrottleWaitText(errObj: Error | NuxtError): string {
    const header = (errObj as { response?: Response }).response?.headers?.get?.('Retry-After');
    const seconds = Number(header);

    if (!header || !Number.isFinite(seconds) || seconds <= 0) {
        return 'in a little while';
    }

    if (seconds <= 90) {
        return 'in a moment';
    }

    if (seconds < 45 * 60) {
        return `in about ${Math.round(seconds / 60)} minutes`;
    }

    const hours = Math.round(seconds / 3600);

    return hours <= 1 ? 'in about an hour' : `in about ${hours} hours`;
}

export function useReview() {
    const apiBasePath = `/v1/reviews`;
    const useApiUtilsObj = useApiUtils();
    const $_fetch = useCustomFetch();

    // TODO: Maybe use getPagedListItems instead
    async function getReviews(
        args: PaginatedRequest = {},
        query: NitroFetchOptions<string>['query'] = {}
    ): Promise<[PaginatedPayload<Review>, ReviewSummary] | Error> {
        const url = args?.url ?? `${apiBasePath}/`;

        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: {}
        };

        lo_mergeCompat(config.query, args?.params, query, {
            // Base params here
        });

        let paginatedPayload: PaginatedPayload<Review>;

        try {
            const response = await $_fetch<CustomPagination<Review> & { summary: ReviewSummary }>(url, config);

            paginatedPayload = {
                pagination: lo_omit(response, ['results', 'summary']),
                data: response.results
            };

            return [paginatedPayload, response.summary];
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }
    
    async function createReview(reviewObj: Review): Promise<ReviewSubmitResponse | NuxtError> {
        const url = `${apiBasePath}/`;
        const config: NitroFetchOptions<string> = {
            method: 'POST',
            body: reviewObj
        };

        try {
            return await $_fetch<ReviewSubmitResponse>(url, config);
        } catch (err) {
            return useApiUtilsObj.getNuxtErrorObj(err);
        }
    }
    
    // The submit endpoint answers with a customer-safe `detail` for a rejected
    // honeypot, a missing token or gibberish, and a flat list of customer-facing
    // strings under `imageHandles` for a photo it would not accept. Both name the
    // remedy, so show them as they are rather than keeping a parallel copy here.
    // The 429 is DRF's own wording ('Expected available in 3600 seconds'), so
    // that one still gets rewritten.
    function getSubmitErrorMessage(errObj: Error | NuxtError, fallback: string): string {
        if (useApiUtilsObj.isErrorOfStatusCode(errObj, 429)) {
            return `You have submitted several reviews recently. Please try again ${getThrottleWaitText(errObj)}.`;
        }

        const handleErrors = useApiUtilsObj.getDataFromError(errObj)?.imageHandles;

        if (Array.isArray(handleErrors)) {
            const messages = handleErrors.filter((msg) => lo_isString(msg));

            if (messages.length) {
                return messages.join(' ');
            }
        }

        return useApiUtilsObj.tryExtractObjFromError(errObj) ?? fallback;
    }

    return {
        getReviews,
        createReview,
        getSubmitErrorMessage
    };
}
